import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import throttle from 'lodash/throttle';
import { Box } from '../Box';
import { useSaveListPosition } from './useSaveListPosition';

export type VirtualFeedProps<T extends { id: string }> = {
    items: T[];
    containerHeight: number;
    getHeight: (item: T) => number;
    renderItem: (params: { item: T; index: number; isActive?: boolean }) => React.ReactNode;
    // Количество элементов, которые будут отображаться за пределами viewPort-а
    indexOffset?: number;
    containerStyles?: React.CSSProperties;
};

const THROTTLE_DELAY = 33;

export const VirtualFeed = <T extends { id: string }>({
    items,
    getHeight,
    renderItem,
    containerHeight,
    containerStyles = {},
    indexOffset = 1,
}: VirtualFeedProps<T>) => {
    const isScrollHappenedRef = useRef(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const viewContainerRef = useRef<HTMLDivElement>(null);
    const topHeightMapRef = useRef<{ [key: string]: number }>({});
    const scrollTopRef = useRef(0);

    const [{ startIndex, endIndex }, setIndexes] = useState({ startIndex: 0, endIndex: 2 });
    const [innerHeight, setInnerHeight] = useState(0);
    const renderItems: JSX.Element[] = [];

    useSaveListPosition({
        viewContainerRef,
        topHeightMapRef,
        scrollTopRef,
        setIndexes,
        startIndex,
        endIndex,
    });

    const checkActiveItemIndex = useCallback(
        (startIndex: number, endIndex: number) => {
            const midLine = viewContainerRef.current?.offsetHeight! / 2;

            for (let i = startIndex - indexOffset; i <= endIndex + indexOffset; i++) {
                const item = items[i];

                if (!item) {
                    continue;
                }

                if (topHeightMapRef.current[i] === 0 && scrollTopRef.current === 0) {
                    return i;
                }

                const postHeight = getHeight(items[i]);

                if (
                    topHeightMapRef.current[i] <= scrollTopRef.current + midLine &&
                    topHeightMapRef.current[i] + postHeight >= scrollTopRef.current + midLine
                ) {
                    return i;
                }
            }
        },
        [indexOffset, items],
    );

    const onScroll = useCallback(
        throttle((e: Event) => {
            setIndexes(({ startIndex, endIndex }) => {
                const scrollTop = (e.target as unknown as { scrollTop: number }).scrollTop;

                let newstartIndex: number = startIndex;
                let newendIndex: number = endIndex;

                // скролл вниз
                if (scrollTop > scrollTopRef.current) {
                    // Проверка верхней карточки
                    if (scrollTop > topHeightMapRef.current[startIndex + 1]) {
                        newstartIndex++;
                    }

                    // Проверка нижней карточки
                    if (scrollTop + containerHeight > topHeightMapRef.current[endIndex + 1]) {
                        newendIndex++;
                    }
                }

                // скролл вверх
                if (scrollTop < scrollTopRef.current) {
                    // Проверка верхней карточки
                    if (scrollTop < topHeightMapRef.current[startIndex]) {
                        newstartIndex--;
                    }

                    // Проверка нижней карточки
                    if (scrollTop + containerHeight < topHeightMapRef.current[endIndex]) {
                        newendIndex--;
                    }
                }

                isScrollHappenedRef.current = true;
                scrollTopRef.current = scrollTop;

                return { startIndex: newstartIndex, endIndex: newendIndex };
            });
        }, THROTTLE_DELAY),
        [containerHeight],
    );

    useLayoutEffect(() => {
        if (!viewContainerRef.current) {
            return;
        }

        viewContainerRef.current.addEventListener('scroll', onScroll);

        return () => {
            viewContainerRef.current?.removeEventListener('scroll', onScroll);
        };
    }, [onScroll]);

    // Effect заполняет мапу высот карточек
    useEffect(() => {
        if (!items.length) {
            return;
        }

        topHeightMapRef.current[0] = 0;
        const mapRows = Object.keys(topHeightMapRef.current).length;

        for (let i = mapRows; i < items.length; i++) {
            const lastItemHeight = getHeight(items[i - 1]);

            topHeightMapRef.current[i] = Number(
                (topHeightMapRef.current[i - 1] + lastItemHeight).toFixed(2),
            );

            if (i === items.length - 1) {
                setInnerHeight(topHeightMapRef.current[i] + lastItemHeight);
            }
        }
    }, [items]);

    const activeItemIndex = checkActiveItemIndex(startIndex, endIndex);

    // При каждом изменении индексов происходит rerender, здесь мы формируем новый массив renderItems
    for (let i = startIndex - indexOffset; i <= endIndex + indexOffset; i++) {
        const item = items[i];

        if (!item) {
            continue;
        }

        const postHeight = getHeight(items[i]);

        renderItems.push(
            <Box
                key={item.id}
                position="absolute"
                top={`${topHeightMapRef.current[i]}px`}
                height={postHeight}
                width="100%"
                scrollSnapAlign="center"
                scrollSnapStop="always"
            >
                {renderItem({
                    item,
                    index: i,
                    isActive: activeItemIndex === i,
                })}
            </Box>,
        );
    }

    return (
        <Box
            ref={viewContainerRef}
            style={containerStyles}
            height={Number(containerHeight.toFixed(2))}
            overflowY={'scroll'}
            // Включаем снепскролл только после первого скролла, чтобы браузер не смещал карточки
            scrollSnapType={isScrollHappenedRef.current ? 'y mandatory' : undefined}
        >
            <Box width="100%" height={innerHeight} position="relative" ref={scrollContainerRef}>
                {renderItems}
            </Box>
        </Box>
    );
};
